import React, { useEffect, useState } from 'react';

const AD_CONFIG_URL = '/api/ads/config';
const ROTATION_MS = 30_000;

// House ad shown when no advertiser is configured — keeps the slot visibly
// populated and gives prospective advertisers something to point at.
const HOUSE_AD = {
  enabled: true,
  advertiser_name: 'PropScope',
  strapline: 'Advertise here — investor-grade audience, daily UK property data. Get in touch.',
  cta_label: 'Advertise with us',
  cta_url: 'https://geekybee.net/contact?ref=propscope-house-ad',
  logo_url: '',
  colour_1: '#0f172a',
  colour_2: '#1e293b',
  text_colour: '#f8fafc',
  is_house_ad: true,
};

// Wall-clock-based rotation: every visitor lands on the same ad at the same
// moment, and refreshing the page picks up wherever the current slot is —
// not back at index 0 — so ads near the end of the list still get airtime.
const wallClockIndex = (count) =>
  count > 0 ? Math.floor(Date.now() / ROTATION_MS) % count : 0;

export default function AdBar() {
  const [ads, setAds] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    fetch(AD_CONFIG_URL, { signal: controller.signal })
      .then(r => r.json())
      .then(data => {
        // Server returns an array of enabled ads. Old single-object responses
        // are wrapped for safety against a stale cache.
        const list = Array.isArray(data) ? data : (data && data.enabled ? [data] : []);
        setAds(list.length ? list : [HOUSE_AD]);
      })
      .catch(() => setAds([HOUSE_AD]));
    return () => controller.abort();
  }, []);

  // Re-render every 30s aligned to the wall clock. The first tick fires on
  // the next 30-second boundary so all visitors transition together.
  useEffect(() => {
    if (!ads || ads.length <= 1) return;
    const msToNextSlot = ROTATION_MS - (Date.now() % ROTATION_MS);
    let interval;
    const firstTimeout = setTimeout(() => {
      setTick(t => t + 1);
      interval = setInterval(() => setTick(t => t + 1), ROTATION_MS);
    }, msToNextSlot);
    return () => {
      clearTimeout(firstTimeout);
      if (interval) clearInterval(interval);
    };
  }, [ads]);

  if (!ads || !ads.length) return null;

  const ad = ads[wallClockIndex(ads.length)];

  const bgStyle = {
    background: `linear-gradient(to right, ${ad.colour_1 || '#1a1a2e'}, ${ad.colour_2 || '#1a1a2e'})`,
    color: ad.text_colour || '#ffffff',
  };

  return (
    <aside
      aria-label="Advertisement"
      style={{ height: '50px', zIndex: 9999, ...bgStyle }}
      className="fixed bottom-0 left-0 right-0 flex items-center overflow-hidden"
    >
      <div className="flex items-center w-full px-4 lg:pl-64 gap-3">
        {ad.logo_url && (
          <img
            src={ad.logo_url}
            alt={ad.advertiser_name || 'Advertiser'}
            className="h-7 w-auto object-contain flex-shrink-0"
          />
        )}
        <p
          className="flex-1 text-center font-medium leading-tight line-clamp-2 lg:line-clamp-1 text-[10px] lg:text-sm"
          style={{ color: ad.text_colour || '#ffffff' }}
        >
          {ad.strapline}
        </p>
        {ad.cta_url?.startsWith('http') && ad.cta_label && (
          <a
            href={ad.cta_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
          >
            {ad.cta_label} →
          </a>
        )}
      </div>
      {ads.length > 1 && (
        <span className="absolute bottom-0 right-2 text-[9px] opacity-50" style={{ color: ad.text_colour || '#fff' }}>
          {wallClockIndex(ads.length) + 1}/{ads.length}
        </span>
      )}
    </aside>
  );
}
